import { __assign, __read } from "tslib";
import { useCurrentRoute } from '@office-start/component-context-service';
import { useFeatureGates } from '@office-start/feature-gates-service';
import { getFileTypeFromAppName } from '@office-start/file-utility';
import { usePrevious } from '@office-start/hooks-utility';
import { useIdentity } from '@office-start/identity-service';
import { logFeature } from '@office-start/telemetry-service';
import { HarmonyAppThemes, HarmonyDefaultThemes, OfficeThemeMode, } from '@office-start/theming-types';
import { useUserEntitlements } from '@office-start/user-entitlements-service';
import { useHarmonyTheme as useVisualHarmonyTheme } from '@office-start/visual-data-service';
import { useEffect, useMemo } from 'react';
import { setHarmonyTheme } from '../operations';
import { getUserAppTheme, isHarmonyThemeId, resolveThemeMode } from '../util';
import { useSetHarmonyThemeData } from './useHarmonyThemeData';
import { useShellTheme } from './useShellTheme';
import { useUpdateShellHeader } from './useUpdateShellHeader';
import { useUpdateVisualThemeMode } from './useUpdateVisualThemeMode';
/**
 * Return the correct theme for a given app
 * @returns An app's theme if on an app page, otherwise undefined.
 */
export var useAppTheme = function () {
    var route = useCurrentRoute();
    return useMemo(function () {
        var _a;
        if (!(route === null || route === void 0 ? void 0 : route.app)) {
            return undefined;
        }
        var fileType = getFileTypeFromAppName(route.app);
        if (!fileType) {
            return undefined;
        }
        return (_a = HarmonyAppThemes[fileType]) !== null && _a !== void 0 ? _a : undefined;
    }, [route]);
};
export var useDefaultHarmonyTheme = function () {
    var _a, _b;
    var featureGatesResult = (_a = useFeatureGates().data) === null || _a === void 0 ? void 0 : _a.result;
    var m365DefaultThemeEnabled = (_b = featureGatesResult === null || featureGatesResult === void 0 ? void 0 : featureGatesResult.m365DefaultThemeGates) === null || _b === void 0 ? void 0 : _b.m365DefaultThemeEnabled;
    return useMemo(function () {
        return m365DefaultThemeEnabled
            ? HarmonyDefaultThemes.Default
            : HarmonyDefaultThemes.Legacy;
    }, [m365DefaultThemeEnabled]);
};
var usePreferOfficeTheme = function (shellTheme) {
    var _a;
    var entitlements = (_a = useUserEntitlements().data) === null || _a === void 0 ? void 0 : _a.result;
    var identity = useIdentity().data;
    return useMemo(function () {
        if (identity === null || identity === void 0 ? void 0 : identity.isMsa) {
            return entitlements === null || entitlements === void 0 ? void 0 : entitlements.PreferOfficeTheme;
        }
        return shellTheme === null || shellTheme === void 0 ? void 0 : shellTheme.preferOfficeTheme;
    }, [
        entitlements === null || entitlements === void 0 ? void 0 : entitlements.PreferOfficeTheme,
        identity === null || identity === void 0 ? void 0 : identity.isMsa,
        shellTheme === null || shellTheme === void 0 ? void 0 : shellTheme.preferOfficeTheme,
    ]);
};
/**
 * Retrieve the user app theme shades from the entitlements. These colors are set by tenant admins in the admin portal.
 * Respect these colors when in light mode and when on the default (or base) theme
 * @returns AppThemeColors if the user has a tenant theme, otherwise undefined.
 */
var useUserAppTheme = function () {
    var _a;
    var entitlements = (_a = useUserEntitlements().data) === null || _a === void 0 ? void 0 : _a.result;
    var defaultTheme = useDefaultHarmonyTheme();
    return useMemo(function () { return getUserAppTheme(defaultTheme, entitlements); }, [defaultTheme, entitlements]);
};
var useHarmonyTheme = function () {
    var _a;
    var _b = __read(useVisualHarmonyTheme(), 1), visualThemeData = _b[0];
    var harmonyDefaultTheme = useDefaultHarmonyTheme();
    var appTheme = useAppTheme();
    var shellTheme = useShellTheme();
    var userShellAppTheme = useUserAppTheme();
    var preferOfficeTheme = usePreferOfficeTheme(shellTheme);
    var entitlements = (_a = useUserEntitlements().data) === null || _a === void 0 ? void 0 : _a.result;
    // Resolve existing theme...
    var resolvedTheme = useMemo(function () {
        var resolvedMode = resolveThemeMode({
            entitlements: entitlements,
            harmonyThemeModeVisuals: visualThemeData.mode,
            shellTheme: shellTheme,
            userShellAppTheme: userShellAppTheme,
        });
        var shellOrHarmonyTheme = shellTheme !== null && shellTheme !== void 0 ? shellTheme : harmonyDefaultTheme;
        if (preferOfficeTheme) {
            shellOrHarmonyTheme = harmonyDefaultTheme;
        }
        else {
            // Only set user colors in default mode and if the theme id is the default or base theme
            if (userShellAppTheme &&
                resolvedMode === OfficeThemeMode.default &&
                (isHarmonyThemeId(shellOrHarmonyTheme.id) ||
                    shellOrHarmonyTheme.id === 'base')) {
                shellOrHarmonyTheme = __assign(__assign({}, shellOrHarmonyTheme), { colors: __assign(__assign({}, userShellAppTheme.colors), { DarkTheme: userShellAppTheme.colors }) });
            }
        }
        return __assign(__assign({}, (appTheme !== null && appTheme !== void 0 ? appTheme : shellOrHarmonyTheme)), { mode: resolvedMode });
    }, [
        appTheme,
        entitlements,
        harmonyDefaultTheme,
        preferOfficeTheme,
        shellTheme,
        userShellAppTheme,
        visualThemeData.mode,
    ]);
    // Update shell mode
    useUpdateVisualThemeMode({
        shellTheme: shellTheme,
        visualThemeData: visualThemeData,
        entitlements: entitlements,
        harmonyThemeData: resolvedTheme,
    });
    return resolvedTheme;
};
/**
 * Updates scroll bar color to dark in dark mode, or light in light mode
 * @param scrollContentRef Scrollbar to update
 * @param theme theme to apply
 */
var useUpdateScrollBarColor = function (scrollContentRef, theme) {
    useEffect(function () {
        if (scrollContentRef === null || scrollContentRef === void 0 ? void 0 : scrollContentRef.current) {
            scrollContentRef.current.style.colorScheme =
                theme.mode === OfficeThemeMode.default ? 'light' : 'dark';
        }
    }, [theme.mode, scrollContentRef]);
};
export var useHarmonyTheming = function (scrollContentRef) {
    var updatedTheme = useHarmonyTheme();
    var previousTheme = usePrevious(updatedTheme);
    var setHarmonyThemeData = useSetHarmonyThemeData();
    useEffect(function () {
        if (!previousTheme ||
            (previousTheme &&
                (previousTheme.mode !== updatedTheme.mode ||
                    previousTheme.id !== updatedTheme.id))) {
            // Update OfficeFluentProvider, and observables that power useThemeMode and useThemeId
            setHarmonyTheme(updatedTheme);
            // Update HarmonyThemeDataContext
            setHarmonyThemeData(updatedTheme);
            logFeature({
                eventName: 'ThemeChanged',
                featureName: "Theming" /* KnownFeatureNames.Theming */,
                params: {
                    ThemeId: updatedTheme.id,
                    ThemeMode: updatedTheme.mode,
                    ThemeType: updatedTheme.type,
                },
            });
        }
    }, [updatedTheme, setHarmonyThemeData, previousTheme]);
    useUpdateScrollBarColor(scrollContentRef, updatedTheme);
    // Respond to changes in theme by updating the header text and namespace
    useUpdateShellHeader();
};
//# sourceMappingURL=useHarmonyTheming.js.map